/**
 * VO Product Options (BCPO) Cart Drawer Integration
 * 
 * This script detects when VO Product Options shows its confirmation popup
 * and replaces it with the theme's cart drawer for a seamless user experience.
 * 
 * Monitors for: .bcpo-modal-parent.bcpo-modal-active
 * Action: Hide popup, update cart UI, and open cart drawer instead
 */

import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class VOPopupCartDrawerIntegration {
  constructor() {
    this.observer = null;
    this.isProcessing = false;
    this.init();
  }

  init() {
    console.log('[VO Integration] Initializing VO popup to cart drawer integration');
    
    // Start monitoring for VO popup immediately
    this.startPopupMonitoring();
    
    // Also listen for DOM changes in case VO loads dynamically
    this.observeDocumentChanges();
  }

  startPopupMonitoring() {
    // Use MutationObserver to watch for the VO popup
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Check for added nodes
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.checkForVOPopup(node);
            }
          });
        }
        
        // Check for class changes on existing elements
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          this.checkForVOPopup(mutation.target);
        }
      });
    });

    // Start observing
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    // Also check for existing popups
    this.checkExistingPopups();
  }

  checkExistingPopups() {
    const existingPopup = document.querySelector('.bcpo-modal-parent.bcpo-modal-active');
    if (existingPopup) {
      console.log('[VO Integration] Found existing VO popup, processing...');
      this.handleVOPopup(existingPopup);
    }
  }

  checkForVOPopup(element) {
    // Check if this element is the VO popup
    if (element.classList && 
        element.classList.contains('bcpo-modal-parent') && 
        element.classList.contains('bcpo-modal-active')) {
      console.log('[VO Integration] VO popup detected!');
      this.handleVOPopup(element);
    }
    
    // Also check child elements
    const voPopup = element.querySelector && element.querySelector('.bcpo-modal-parent.bcpo-modal-active');
    if (voPopup) {
      console.log('[VO Integration] VO popup found in child elements');
      this.handleVOPopup(voPopup);
    }
  }

  async handleVOPopup(popupElement) {
    if (this.isProcessing) {
      console.log('[VO Integration] Already processing, skipping...');
      return;
    }

    this.isProcessing = true;
    console.log('[VO Integration] Processing VO popup...');

    try {
      // Hide the VO popup immediately
      this.hideVOPopup(popupElement);
      
      // Small delay to ensure cart is updated by VO
      await this.delay(300);
      
      // Fetch updated cart data and sections
      await this.updateCartUI();
      
      // Open the cart drawer
      this.openCartDrawer();
      
      console.log('[VO Integration] Successfully replaced VO popup with cart drawer');
      
    } catch (error) {
      console.error('[VO Integration] Error handling VO popup:', error);
    } finally {
      // Reset processing flag after a delay
      setTimeout(() => {
        this.isProcessing = false;
      }, 1000);
    }
  }

  async updateCartUI() {
    console.log('[VO Integration] Updating cart UI...');
    
    try {
      // Fetch the current cart to get updated data
      const cartResponse = await fetch('/cart.js', {
        ...fetchConfig(),
        method: 'GET'
      });

      if (!cartResponse.ok) {
        throw new Error('Failed to fetch cart data');
      }

      const cartData = await cartResponse.json();
      console.log('[VO Integration] Cart data fetched:', cartData);

      // Fetch cart sections to ensure proper rendering with preserved styling context
      const sectionsToRender = ['cart-drawer', 'cart-icon-bubble'];
      const sectionsResponse = await fetch(`${window.location.pathname}?${sectionsToRender.map(section => `sections=${section}`).join('&')}`);
      
      if (!sectionsResponse.ok) {
        throw new Error('Failed to fetch cart sections');
      }
      
      const sectionsData = await sectionsResponse.json();
      console.log('[VO Integration] Sections data fetched:', sectionsData);

      // Dispatch the CartAddEvent to trigger the theme's cart update mechanism
      const cartAddEvent = new CartAddEvent(cartData, 'vo-integration', {
        source: 'vo-popup-integration',
        itemCount: cartData.item_count,
        productId: this.getLastAddedProductId(cartData),
        sections: sectionsData,
        didError: false
      });

      console.log('[VO Integration] Dispatching CartAddEvent:', cartAddEvent);
      document.dispatchEvent(cartAddEvent);

    } catch (error) {
      console.error('[VO Integration] Error updating cart UI:', error);
      // Still try to open cart drawer even if update fails
    }
  }

  getLastAddedProductId(cartData) {
    // Try to get the product ID from the last item in the cart
    if (cartData.items && cartData.items.length > 0) {
      const lastItem = cartData.items[cartData.items.length - 1];
      return lastItem.product_id?.toString() || '';
    }
    return '';
  }

  hideVOPopup(popupElement) {
    console.log('[VO Integration] Hiding VO popup...');
    
    // Method 1: Remove the active class
    if (popupElement.classList.contains('bcpo-modal-active')) {
      popupElement.classList.remove('bcpo-modal-active');
    }
    
    // Method 2: Hide with CSS
    popupElement.style.display = 'none';
    popupElement.style.visibility = 'hidden';
    popupElement.style.opacity = '0';
    
    // Method 3: Try to find and click close button
    const closeButton = popupElement.querySelector('.bcpo-modal-close, .close, [data-dismiss], .modal-close');
    if (closeButton) {
      console.log('[VO Integration] Found close button, clicking...');
      closeButton.click();
    }
    
    // Method 4: Remove backdrop/overlay if present
    const backdrop = document.querySelector('.bcpo-modal-backdrop, .modal-backdrop, .bcpo-overlay');
    if (backdrop) {
      backdrop.style.display = 'none';
    }
    
    // Method 5: Try to remove the entire popup after a delay
    setTimeout(() => {
      if (popupElement.parentNode) {
        popupElement.remove();
      }
    }, 100);
  }

  openCartDrawer() {
    console.log('[VO Integration] Opening cart drawer...');
    
    // Method 1: Use the cart drawer component directly (preferred)
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer) {
      if (typeof cartDrawer.open === 'function') {
        console.log('[VO Integration] Using cartDrawer.open() method');
        cartDrawer.open();
        return;
      }
      
      if (typeof cartDrawer.showDialog === 'function') {
        console.log('[VO Integration] Using cartDrawer.showDialog() method');
        cartDrawer.showDialog();
        return;
      }
    }
    
    // Method 2: Click the cart button
    const cartButton = document.querySelector('[data-testid="cart-icon"]') || 
                      document.querySelector('.header-actions__action') ||
                      document.querySelector('button[on\\:click="/open"]') ||
                      document.querySelector('.cart-drawer button');
    
    if (cartButton) {
      console.log('[VO Integration] Clicking cart button');
      cartButton.click();
      return;
    }
    
    // Method 3: Dispatch a custom event to trigger cart drawer
    console.log('[VO Integration] Dispatching custom cart open event');
    const event = new CustomEvent('vo:open-cart-drawer', {
      bubbles: true,
      detail: { source: 'vo-integration' }
    });
    document.dispatchEvent(event);
  }

  observeDocumentChanges() {
    // Additional observer for dynamic content loading
    const documentObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if VO-related elements were added
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList && (
                  node.classList.contains('bcpo-modal-parent') ||
                  node.querySelector && node.querySelector('.bcpo-modal-parent')
                )) {
                console.log('[VO Integration] VO elements detected in DOM');
                // Re-check for popups after a short delay
                setTimeout(() => this.checkExistingPopups(), 100);
              }
            }
          });
        }
      });
    });

    documentObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    console.log('[VO Integration] Integration destroyed');
  }
}

// Initialize when DOM is ready
function initVOIntegration() {
  console.log('[VO Integration] DOM ready, initializing...');
  new VOPopupCartDrawerIntegration();
}

// Handle different loading states
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVOIntegration);
} else {
  // DOM is already ready
  initVOIntegration();
}

// Also initialize after a delay to catch late-loading VO scripts
setTimeout(initVOIntegration, 2000);

export default VOPopupCartDrawerIntegration;
